/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import {
	CosmosComponentContextProps,
	getHero,
	getSections,
	heroOwnsHeading,
	useCosmosComponentContext,
} from "@cosmos/components";
import { PageHeroesFragment, RefSectionsFragment } from "@cosmos/hygraph-schema";
import { useAuth, useSilentLogin } from "@cosmos/next-hooks";

import { TemplateProviders } from "./templates/TemplateProviders";

interface BasePageData {
	hero?: PageHeroesFragment | null;
	refSections?: RefSectionsFragment[] | null;
}

interface Props<T> {
	pageData?: T;
	componentContext?: CosmosComponentContextProps;
}

export const ClientPageProviders = <T extends BasePageData>({
	pageData,
	componentContext,
}: Props<T>): React.JSX.Element => {
	const contextFromProvider = useCosmosComponentContext();
	const ctx = componentContext ?? contextFromProvider;
	useSilentLogin(useAuth(!ctx.enableAuth));

	// A hero that renders the page's h1 owns it — tell getSections so the first ref-section
	// nests as h2 instead of emitting a second h1. But a purely decorative hero (e.g. a glow
	// with only a background image, no title/CTA) renders NO h1, so it must NOT suppress the
	// promotion — otherwise the page has no h1 at all (the /download case). Gate on
	// heroOwnsHeading (hero has heading content), not mere hero presence.
	const hero = getHero(pageData?.hero);

	return (
		<TemplateProviders componentContext={ctx}>
			{hero}
			{getSections(pageData?.refSections, undefined, heroOwnsHeading(pageData?.hero))}
		</TemplateProviders>
	);
};
